/**
 * Display-only SEIS marks and the stale-Pending check.
 * None of these write plan, plan status, or dates.
 */

import { isSeisExited, normalizeSeisEligibilityStatus } from './planEvidence.js'
import { normalizeSeisDate, parseMeetingTypeCodes } from './seisImport.js'

/** Plan Type 100 (or the words "IEP") with the initial wording excluded. */
export function isActiveIepPlanType(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return false
  if (/^\s*I?300\b/i.test(raw) || /\binitial\b/i.test(raw)) return false
  return /^\s*I?100\b/i.test(raw) || /\bIEP\b/i.test(raw)
}

/**
 * Pending in SEIS, but the row already has an IEP: Plan Type 100, or an
 * affirmed date. Correct the status in SEIS. Do not open an evaluation.
 */
export function isStaleSeisPending({
  eligibilityStatus = '',
  planType = '',
  affirmedIepDate = ''
} = {}) {
  const status = normalizeSeisEligibilityStatus(eligibilityStatus)
  if (!status.pending) return false
  if (String(affirmedIepDate ?? '').trim()) return true
  return isActiveIepPlanType(planType)
}

function todayIso() {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

/**
 * The future-form meeting date is after the date SEIS locked the current IEP,
 * and the meeting has happened. A scheduled meeting next month is not a flag —
 * there is nothing to affirm yet.
 */
export function isUnaffirmedMeeting(futureIepMeetingDate, affirmedIepDate, { today } = {}) {
  const future = normalizeSeisDate(futureIepMeetingDate)
  const affirmed = normalizeSeisDate(affirmedIepDate)
  if (!future || !affirmed) return false
  return future > affirmed && future <= (today || todayIso())
}

/**
 * The case manager cleared the "Not affirmed" mark for THIS occurrence —
 * the dismissal stores the future-meeting date it was based on, so a later
 * import with a different unaffirmed meeting brings the mark right back.
 * A new affirmed date resolves the mark on its own and the stale dismissal
 * just stops mattering.
 */
export function isUnaffirmedCleared(student = {}) {
  const cleared = student?.app?.flags?.unaffirmedCleared
  const clearedDate = normalizeSeisDate(cleared?.futureIepMeetingDate)
  if (!clearedDate) return false
  return clearedDate === normalizeSeisDate(student?.seis?.futureIepMeetingDate)
}

/**
 * Marks for one student after a SEIS import. BIP No stays quiet.
 * Signature Current IEP is stored and not marked: a No often means consent
 * was recorded on an amendment, so "unsigned" misstates the plan.
 */
export function seisStudentMarks(student = {}, { hasBipFile = false } = {}) {
  const seis = student?.seis || {}
  const bip = String(seis.bip ?? '')
    .trim()
    .toLowerCase()
  const unaffirmed =
    isUnaffirmedMeeting(seis.futureIepMeetingDate, seis.affirmedIepDate) &&
    !isUnaffirmedCleared(student)
  return {
    bipMissing: bip === 'yes' && !hasBipFile,
    unaffirmed,
    // The held-but-unaffirmed meeting, normalized (YYYY-MM-DD) — shown next to
    // the PR date so "new PR date, but the IEP behind it isn't locked yet"
    // reads as one fact.
    unaffirmedDate: unaffirmed ? normalizeSeisDate(seis.futureIepMeetingDate) : ''
  }
}

export function meetingTypeBlocksInitial(raw) {
  if (raw == null) return false
  const codes = parseMeetingTypeCodes(raw)
  if (!codes.length) return false
  return !codes.includes('30')
}

export function eligibilityBlocksInitial(raw) {
  if (raw == null || String(raw).trim() === '') return false
  return !normalizeSeisEligibilityStatus(raw).pending
}

export function exitedBlocksInitial(raw) {
  if (raw == null || String(raw).trim() === '') return false
  return isSeisExited(raw)
}
